import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { LookupItem, SavedReport, StringLookupItem } from '@models/lookup';
import { BooleanFilter, DateFilter, MultiSelectFilter, SelectFilter } from '@models/filter-types';
import { LookupV2Service } from '@services/lookup-v2/lookup-v2.service';
import { startTimeFilter } from '@models/base_filters';
import moment from 'moment';
import {
    EmailReportsSelectRecipientsComponent,
    SelectRecipientsData,
} from '@shared/components/report-components/email-reports-select-recipients/email-reports-select-recipients.component';
import { AuthService } from '@services/auth/auth.service';
import { UiConfig } from '@models/config';
import { NotificationService } from '@services/notification/notification.service';
import { UrlStore } from '@shared/helpers/constants/url-store';

const monday = 'Monday';
const nineAm = moment().startOf('day').hour(9).minute(0);

enum Frequency {
    daily = 1,
    weekly = 2,
    monthly = 3,
    quarterly = 4,
}

export const FREQUENCY_INTERVALS = [
    { id: Frequency.daily, name: 'Daily', displayName: 'Daily' },
    { id: Frequency.weekly, name: 'Weekly', displayName: 'Weekly' },
    { id: Frequency.monthly, name: 'Monthly', displayName: 'Monthly' },
    { id: Frequency.quarterly, name: 'Quarterly', displayName: 'Quarterly' },
];

// Do not change this values, these are used to map python now.weekday()
const daysOfWeek = [
    { id: 0, name: 'Monday', displayName: 'Monday' },
    { id: 1, name: 'Tuesday', displayName: 'Tuesday' },
    { id: 2, name: 'Wednesday', displayName: 'Wednesday' },
    { id: 3, name: 'Thursday', displayName: 'Thursday' },
    { id: 4, name: 'Friday', displayName: 'Friday' },
    { id: 5, name: 'Saturday', displayName: 'Saturday' },
    { id: 6, name: 'Sunday', displayName: 'Sunday' },
];

const weekOfMonth = [
    { id: '1', name: '1st', displayName: '1st' },
    { id: '2', name: '2nd', displayName: '2nd' },
    { id: '3', name: '3rd', displayName: '3rd' },
    { id: '4', name: '4th', displayName: '4th' },
    { id: 'L', name: 'Last', displayName: 'Last' },
];

enum DailyOptions {
    mondayToFriday = 'Monday to Friday',
    everyday = 'Everyday',
}

@Component({
    selector: 'app-email-reports-pop-up',
    templateUrl: './email-reports-pop-up.component.html',
    styleUrls: ['./email-reports-pop-up.component.scss'],
    standalone: false,
})
export class EmailReportsPopUpComponent {
    isDaily = false;
    isWeekly = false;
    isMonthly = false;
    isQuarterly = false;
    isSendingReport = false;
    report: SavedReport;
    userIds!: number[];
    public toggleSwitch = new BooleanFilter('Email Report', true).Slider(true);

    isPoliticalLur = false;
    excludePastWeeks = new BooleanFilter('Exclude past weeks', false).Slider(true);

    config!: UiConfig;

    dailyOptions = DailyOptions;

    dailyOption = this.dailyOptions.mondayToFriday;

    frequencyFilter = new SelectFilter<LookupItem>('Frequency').Options(FREQUENCY_INTERVALS).OnChange((item: LookupItem) => {
        if (!item) {
            return;
        }
        this.isDaily = item.id === Frequency.daily;
        this.isWeekly = item.id === Frequency.weekly;
        this.isMonthly = item.id === Frequency.monthly;
        this.isQuarterly = item.id === Frequency.quarterly;
        if (this.isQuarterly) {
            this.dayOfWeekFilter.Value = [this.dayOfWeekFilter.options.find((option) => option.name === monday)];
            const offset = new Date().getTimezoneOffset(); // Gets timezone in minutes
            this.timeFilter.Value = nineAm.utcOffset(offset);
        }
        if (this.isDaily) {
            this.updateDayOfWeek();
        } else {
            this.dayOfWeekFilter.Value = [];
        }
    });

    dayOfWeekFilter = new MultiSelectFilter<LookupItem>('Day of Week').Options(daysOfWeek);

    weekOfMonth = new MultiSelectFilter<StringLookupItem>('Week Number').Options(weekOfMonth);

    timeFilter = startTimeFilter('Time');

    firstDayOfTheMonth = new BooleanFilter('1st day of the month', false).Slider(true);

    endDateFilter = new DateFilter('End Date(OPTIONAL)')
        .Icon('fas fa-calendar-minus')
        .View('Week')
        .IsEndDate(true)
        .Filter((v: moment.Moment) => v >= moment().startOf('day'))
        .Required(false);

    constructor(
        private lookupV2Service: LookupV2Service,
        private authSvc: AuthService,
        public matDialog: MatDialog,
        private snackBarSvc: NotificationService,
        private dialogRef: MatDialogRef<EmailReportsPopUpComponent>,
        @Inject(MAT_DIALOG_DATA)
        public data: {
            report: SavedReport;
            screenName?: string;
        },
    ) {
        this.config = this.authSvc.getConfig();
        this.report = data.report;
        this.isPoliticalLur = data.screenName === UrlStore.screenNames.politicalLur;

        if (this.isPoliticalLur) {
            this.excludePastWeeks.Value = true;
        }

        if (this.report?.emailReports) {
            const emailConfig = this.report.emailReports;
            this.userIds = emailConfig.userIds || [this.report.userId];
            if (emailConfig?.frequencyId) {
                this.frequencyFilter.Value = this.frequencyFilter.options.find((option) => option.id === emailConfig.frequencyId);
            }
            if (emailConfig?.firstDayOfMonth) {
                this.firstDayOfTheMonth.Value = emailConfig.firstDayOfMonth;
            }
            if (emailConfig?.dow) {
                if (emailConfig.frequencyId === 1) {
                    // Daily
                    const isEveryday = emailConfig.dow.length === 7 && [0, 1, 2, 3, 4, 5, 6].every((num) => emailConfig.dow.includes(num));
                    this.dailyOption = isEveryday ? this.dailyOptions.everyday : this.dailyOptions.mondayToFriday;
                }
                this.dayOfWeekFilter.Value = this.dayOfWeekFilter.options.filter((option) => emailConfig.dow.includes(option.id));
            }
            if (emailConfig?.weekOfMonth) {
                this.weekOfMonth.Value = this.weekOfMonth.options.filter((option) => emailConfig.weekOfMonth.includes(option.id));
            }
            if (emailConfig?.time) {
                this.timeFilter.Value = moment(emailConfig.time, 'HH:mm:ss', true);
            }
            if (emailConfig?.endDate) {
                this.endDateFilter.Value = moment(emailConfig.endDate);
            }
            if (emailConfig?.excludePastWeeks != null) {
                this.excludePastWeeks.Value = emailConfig.excludePastWeeks;
            }
            this.toggleSwitch.Value = emailConfig.isActive ?? true;
        }
    }

    validateFilters() {
        const frequencyFilterSelected = this.frequencyFilter.Value;
        const timeFilterSelected = this.timeFilter.Value;
        const dowFilterSelected = this.dayOfWeekFilter.Value.length > 0;
        const weekOfMonthSelected = this.weekOfMonth.Value.length > 0;

        if (this.isDaily) {
            return timeFilterSelected;
        }
        if (this.isWeekly) {
            return dowFilterSelected && timeFilterSelected;
        }
        if (this.isMonthly) {
            if (this.firstDayOfTheMonth.Value) {
                return timeFilterSelected;
            }
            return weekOfMonthSelected && dowFilterSelected && timeFilterSelected;
        }
        return frequencyFilterSelected;
    }

    sendOneTimeReport() {
        if (!this.isSendingReport) {
            this.isSendingReport = true;
            this.lookupV2Service.sendOneTimeReport(this.report.reportId, this.userIds).subscribe(
                () => {
                    this.isSendingReport = false;
                    this.snackBarSvc.openSnackBar('Emailed report will be sent within five minutes', 'success');
                },
                (err) => {
                    this.isSendingReport = false;
                    this.snackBarSvc.openSnackBar('Emailed report failed to send. Please try again or contact support', 'error');
                    console.error(err);
                },
            );
        }
    }

    onSaveFilters() {
        const emailReportId = this.report.emailReports?.emailReportId;
        const reportId = this.report.reportId;
        const frequencyId = this.frequencyFilter.Value.id;
        const dows = this.dayOfWeekFilter.Value.map((dow) => dow.id);
        const time = this.timeFilter && this.timeFilter.Value ? this.timeFilter.Value.format('HH:mm:ss') : null;
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const week = this.weekOfMonth.Value.map((w) => w.id);
        const firstDayOfMonth = this.firstDayOfTheMonth.Value;
        const isActive = this.toggleSwitch.Value;
        const endDate = this.endDateFilter.Value;
        const excludePastWeeks = this.isPoliticalLur ? this.excludePastWeeks.Value : false;
        this.report.emailReports.isActive = isActive;
        if (isActive) {
            this.report.emailReports = {
                emailReportId,
                frequencyId,
                dow: dows,
                time,
                timezone,
                weekOfMonth: week,
                firstDayOfMonth,
                userIds: this.userIds,
                isActive,
                endDate,
                excludePastWeeks,
            };
        }
        this.lookupV2Service
            .saveEmailReportFrequency(
                emailReportId,
                reportId,
                frequencyId,
                dows,
                time,
                timezone,
                week,
                firstDayOfMonth,
                this.userIds || [this.report.userId],
                isActive,
                endDate,
                excludePastWeeks,
            )
            .subscribe(
                (emailReport) => {
                    this.report.emailReports.emailReportId = emailReport;
                    this.dialogRef.close();
                },
                (err) => {
                    console.error(err);
                },
            );
    }

    closeDialogTrue() {
        this.dialogRef.close(true);
    }

    closeDialogFalse() {
        this.dialogRef.close(false);
    }

    openSelectRecipients(): void {
        if (this.config.roleCapability.rate_optics?.general.email_report_to_others) {
            const dialog = this.matDialog.open(EmailReportsSelectRecipientsComponent, {
                data: {
                    channels: this.report.channels,
                    userIds: this.userIds,
                } as SelectRecipientsData,
                width: 'fit-content',
            });

            dialog.afterClosed().subscribe((userIds: number[]) => {
                if (userIds?.length > 0) {
                    this.userIds = userIds;
                }
            });
        }
    }

    updateDayOfWeek(): void {
        const dows = this.dailyOption === this.dailyOptions.everyday ? [0, 1, 2, 3, 4, 5, 6] : [0, 1, 2, 3, 4];
        this.dayOfWeekFilter.Value = this.dayOfWeekFilter.options.filter((option) => dows.includes(option.id));
    }
}
